import type { FamilyCapabilityFlags } from './familyCapabilities';

/**
 * Every navigation entry in the app chrome, keyed so the same table can drive
 * the mobile pill, the desktop sidebar and route access in one place.
 */
export type FamilyNavKey =
  | 'home'
  | 'budget'
  | 'goals'
  | 'calendar'
  | 'insights'
  | 'recurring'
  | 'income'
  | 'documents'
  | 'scanner'
  | 'add'
  | 'goal_contribution'
  | 'family'
  | 'journey'
  | 'roadmap'
  | 'settings';

type CapabilityKey = keyof FamilyCapabilityFlags;

/**
 * The capability a navigation entry needs. `null` means the entry is always
 * available: those areas are personal (Fennie, settings, roadmap) or the
 * member's own group page, which is how they see their role and seat.
 *
 * Only applied while the family scope is selected. The personal account is
 * never reshaped.
 */
export const NAV_CAPABILITY: Record<FamilyNavKey, CapabilityKey | null> = {
  home: 'canViewExpenses',
  budget: 'canViewBudget',
  goals: 'canViewGoals',
  calendar: 'canViewExpenses',
  insights: 'canViewInsights',
  recurring: 'canViewExpenses',
  income: 'canViewIncome',
  documents: 'canScanReceipt',
  scanner: 'canScanReceipt',
  add: 'canAddExpense',
  goal_contribution: 'canContributeGoals',
  family: null,
  journey: null,
  roadmap: null,
  settings: null,
};

/** Every nav key the gate knows about, in menu order. */
export const ALL_FAMILY_NAV_KEYS: FamilyNavKey[] = Object.keys(NAV_CAPABILITY) as FamilyNavKey[];

const PATH_PREFIXES: [string, FamilyNavKey][] = [
  ['/dashboard', 'home'],
  ['/budget', 'budget'],
  ['/goals', 'goals'],
  ['/calendar', 'calendar'],
  ['/insights', 'insights'],
  ['/upcoming', 'recurring'],
  ['/recurring', 'recurring'],
  ['/income', 'income'],
  ['/scheduled', 'recurring'],
  ['/documents', 'documents'],
  ['/scanner', 'scanner'],
  ['/add', 'add'],
  ['/family', 'family'],
  ['/journey', 'journey'],
  ['/roadmap', 'roadmap'],
  ['/settings', 'settings'],
];

/** Maps a pathname to the navigation entry that owns it. */
export function navKeyForPath(pathname: string): FamilyNavKey | null {
  const p = (pathname || '/').toLowerCase();
  if (p === '/' || p === '') return 'home';
  if (p.startsWith('/goals/join/')) return null;
  for (const [prefix, key] of PATH_PREFIXES) {
    // Screens in this app are named like /budget-details, so a hyphen counts
    // as a segment boundary too. Without it those pages would look unmapped
    // and stay open to every role.
    if (p === prefix || p.startsWith(`${prefix}/`) || p.startsWith(`${prefix}-`)) return key;
  }
  return null;
}

/** The capability a route needs, or null when it is open to every member. */
export function requiredCapabilityForPath(pathname: string): CapabilityKey | null {
  const key = navKeyForPath(pathname);
  return key ? NAV_CAPABILITY[key] : null;
}

/** True when a navigation entry should be rendered for the active scope. */
export function canUseNavKey(
  key: FamilyNavKey,
  caps: FamilyCapabilityFlags,
  isFamilyScope: boolean,
): boolean {
  if (!isFamilyScope) return true;
  const capability = NAV_CAPABILITY[key];
  return capability ? !!caps[capability] : true;
}

/** True when the route may render its content for the active scope. */
export function isRouteAllowedForPath(
  pathname: string,
  caps: FamilyCapabilityFlags,
  isFamilyScope: boolean,
): boolean {
  if (!isFamilyScope) return true;
  const capability = requiredCapabilityForPath(pathname);
  return capability ? !!caps[capability] : true;
}
